import { defineComponent as _defineComponent } from 'vue'
import router from "@/router";
  import { computed, onMounted, ref, watch } from "vue";
  import { useStore } from "vuex";
  import { formatDate } from "@/helpers/date";
  import DropDown from "@/components/ui/dropdown/DropDown.vue";
  import RemoveDocumentsModal from "@/components/ui/modals/RemoveDocumentModal.vue";
  import CreateFolderModal from "@/components/ui/modals/CreateFolderModal.vue";
  import ImagePreviewModal from "@/components/ui/modals/ImagePeviewModal.vue";
  import ShareLotsModal from "@/components/ui/modals/ShareLotsModal.vue";
  import ShareStageModal from "@/components/ui/modals/ShareStageModal.vue";
  import {
    getDocumentMimeType,
    isImage,
    previewIcon,
  } from "@/helpers/documents";
  import { downloadFile } from "@/helpers/downloadFile";
  import { UserRoles } from "@/enums/UserRoles";
  import plural from "@/features/plural";
  import {
    isValidFileSize,
    isValidDocumentType,
    validDocumentsTypes,
  } from "@/helpers/fileUpload";
  import { useToast } from "vue-toastification";

  type EstateFolder = {
    Prefix: string;
  };

  type EstateFile = {
    Key: string;
    Link: string;
    LastModified: string;
    ETag: string;
  };

  type SharedFile = {
    fileName?: string;
    url?: string;
    modifiedDate?: string;
    ETag?: string;
    mimetype?: string;
  };

  
export default /*@__PURE__*/_defineComponent({
  __name: 'DocumentsView',
  setup(__props, { expose: __expose }) {
  __expose();

  const store = useStore();
  const toast = useToast();
  const estateId = computed(() => {
    return (router.currentRoute.value.meta.access as string[]).includes(
      UserRoles.SUPER_ADMIN
    )
      ? router.currentRoute.value.params.estateId
      : store.state.user.selectedEstateId;
  });

  const folderPath = ref("");
  const docs = ref<{ folders: EstateFolder[]; files: EstateFile[] }>({
    folders: [],
    files: [],
  });
  const fileForDelete = ref({ file: "", index: 0 });
  const isOpenRemoveDocumentsModal = ref(false);
  const getRemoveDocumentType = computed(() =>
    fileForDelete.value.file.endsWith("/") ? "folder" : "file"
  );
  const showCreateFolder = ref(false);
  const fileInput = ref<HTMLInputElement | null>(null);
  const documentTypes = validDocumentsTypes.join(", ");
  const showImg = ref<string | undefined>(undefined);

  onMounted(() => {
    getFolder();
  });
  watch(
    () => estateId.value,
    () => {
      getFolder();
    }
  );

  const activeFolder = computed(() => {
    return folderPath.value
      .split("/")
      .filter((element) => {
        return element !== "";
      })
      .slice(-1)[0];
  });

  const loading = ref(false);
  const getFolder = () => {
    if (estateId.value) {
      loading.value = true;
      store
        .dispatch("estate/getEstateFolder", {
          estateId: estateId.value,
          foldername: folderPath.value,
        })
        .then((res) => {
          docs.value = res.data;
        })
        .finally(() => {
          loading.value = false;
        });
    }
  };

  function toFolder(folder: string) {
    folderPath.value = folder;
    getFolder();
  }
  function onBack() {
    const pathParts = folderPath.value.split("/");

    pathParts.splice(pathParts.length - 2, 2);
    const path = pathParts.join("/");

    folderPath.value = path + (path ? "/" : "");
    getFolder();
  }
  function getFolderName(folderPath: string) {
    const pathParts = folderPath.split("/");

    return pathParts[pathParts.length - 2];
  }
  function getFileName(filePath: string) {
    const pathParts = filePath.split("/");

    return pathParts[pathParts.length - 1];
  }
  const uploadFile = (e: Event) => {
    const target = e.target as HTMLInputElement;

    if (!target.files?.length) {
      return;
    }

    const file = target.files[0];

    if (fileInput.value) {
      fileInput.value.value = "";
    }

    if (!isValidDocumentType(file) || !isValidFileSize(file)) {
      return;
    }

    const data = new FormData();

    data.append("file", file);

    if (folderPath.value) {
      data.append("foldername", folderPath.value);
    }

    loading.value = true;
    store
      .dispatch("estate/uploadEstateFile", {
        estateId: estateId.value,
        file: data,
      })
      .then((res) => {
        toast.success("Your document was successfully uploaded");
        docs.value.files.push(res.data);
        docs.value.files.sort((a: EstateFile, b: EstateFile) =>
          a.Key.localeCompare(b.Key, "en-AU", {
            numeric: true,
            sensitivity: "base",
            caseFirst: "upper",
          })
        );
      })
      .catch(() => toast.error("Something went wrong"))
      .finally(() => (loading.value = false));
  };

  function addFolder(folder: EstateFolder) {
    docs.value.folders.push(folder);
    docs.value.folders.sort((a: EstateFolder, b: EstateFolder) =>
      a.Prefix.localeCompare(b.Prefix, "en-AU", {
        numeric: true,
        sensitivity: "base",
        caseFirst: "upper",
      })
    );
  }
  function openFile(file: EstateFile) {
    if (isImage(file.Key)) {
      showImg.value = file.Link;
    } else {
      window.open(file.Link, "_blank");
    }
  }
  function showDeleteFileModal(file: string, index: number) {
    fileForDelete.value = { file, index };
    isOpenRemoveDocumentsModal.value = true;
  }
  const deleteFile = async () => {
    const data = {
      estateId: estateId.value,
      foldername:
        getRemoveDocumentType.value === "folder"
          ? fileForDelete.value.file
          : null,
      filename:
        getRemoveDocumentType.value === "folder"
          ? null
          : fileForDelete.value.file,
    };

    loading.value = true;

    try {
      await store.dispatch("estate/deleteEstateFolder", data);
      isOpenRemoveDocumentsModal.value = false;
      docs.value[`${getRemoveDocumentType.value}s`].splice(
        fileForDelete.value.index,
        1
      );
    } catch {
      toast.error("Something went wrong");
    } finally {
      loading.value = false;
    }
  };

  const showShareModal = ref(false);
  const showShareStageModal = ref(false);
  const fileToShare = ref<SharedFile>({});
  const shareFile = (file: EstateFile) => {
    showShareModal.value = true;
    fileToShare.value = {
      fileName: file.Key,
      url: file.Link,
      modifiedDate: file.LastModified,
      ETag: file.ETag,
      mimetype: getDocumentMimeType(file.Key),
    };
  };
  const shareFileWithStage = (file: EstateFile) => {
    showShareStageModal.value = true;
    fileToShare.value = {
      fileName: file.Key,
      url: file.Link,
      modifiedDate: file.LastModified,
      ETag: file.ETag,
      mimetype: getDocumentMimeType(file.Key),
    };
  };

const __returned__ = { store, toast, estateId, folderPath, docs, fileForDelete, isOpenRemoveDocumentsModal, getRemoveDocumentType, showCreateFolder, fileInput, documentTypes, showImg, activeFolder, loading, getFolder, toFolder, onBack, getFolderName, getFileName, uploadFile, addFolder, openFile, showDeleteFileModal, deleteFile, showShareModal, showShareStageModal, fileToShare, shareFile, shareFileWithStage, get router() { return router }, computed, onMounted, ref, watch, get useStore() { return useStore }, get formatDate() { return formatDate }, DropDown, RemoveDocumentsModal, CreateFolderModal, ImagePreviewModal, ShareLotsModal, ShareStageModal, get getDocumentMimeType() { return getDocumentMimeType }, get isImage() { return isImage }, get previewIcon() { return previewIcon }, get downloadFile() { return downloadFile }, get UserRoles() { return UserRoles }, get plural() { return plural }, get isValidFileSize() { return isValidFileSize }, get isValidDocumentType() { return isValidDocumentType }, get validDocumentsTypes() { return validDocumentsTypes }, get useToast() { return useToast } }
Object.defineProperty(__returned__, '__isScriptSetup', { enumerable: false, value: true })
return __returned__
}

})